/**
 * Messages — the caller's own conversations.
 *
 * Ported from the app's `threads_screen.dart` and `chat_screen.dart`, which
 * are two bottom-nav screens; on a wide portal they are one page with a list
 * beside the conversation, and stack on a phone.
 *
 * *** ONE SCREEN, BOTH ROLES ***
 * `/chat/threads` returns the caller's own conversations whichever side they
 * are on. The server decides; this only changes which NAME it shows.
 *
 * *** NOT THE ADMIN CHAT TABLE ***
 * `/chat-threads` is a support tool listing every conversation in the tenant
 * with no way to read one. This is the participant's view.
 *
 * *** THE LOCK IS PER-VIEWER ***
 * `is_locked` comes from `check_quota(viewer_id=...)`. A lapsed plan blocks
 * BOTH parties, but a mentor must never be shown the student's upsell — so
 * the composer's disabled message is chosen by role, and `upsell_message`
 * is rendered only for a student.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { PageHeader, PageStack, Card } from '@/components/ui/Page'
import { Button } from '@/components/ui/Button'
import { ApiError } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import {
  fetchOlderMessages,
  useMarkThreadRead,
  useMyThreads,
  useSendMessage,
  useThreadMessages,
  useThreadQuota,
} from '@/lib/queries'
import { useToast } from '@/components/ui/Toast'
import { initials } from '@/lib/format'
import type { ChatMessage, ChatThread } from '@/types/api'
import { MessageBubble } from './MessageBubble'
import { Composer } from './Composer'
import styles from './MessagesPage.module.css'

/** "14:05" today, "Mon 14:05" this week, else a date. */
function threadTime(iso: string | null): string {
  if (!iso) return ''
  const when = new Date(iso)
  if (Number.isNaN(when.getTime())) return ''

  const now = new Date()
  const sameDay = when.toDateString() === now.toDateString()
  if (sameDay) {
    return when.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  const days = (now.getTime() - when.getTime()) / 86_400_000
  if (days < 7) {
    return when.toLocaleDateString(undefined, { weekday: 'short' })
  }
  return when.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

export function MessagesPage() {
  const { user } = useAuth()
  const isMentor = user?.role === 'MENTOR'
  const toast = useToast()

  const [openId, setOpenId] = useState<string | null>(null)

  const threads = useMyThreads()
  const messages = useThreadMessages(openId ?? undefined)
  const quota = useThreadQuota(openId ?? undefined)
  const send = useSendMessage(openId ?? undefined)
  const markRead = useMarkThreadRead()

  const list = threads.data?.threads ?? []

  /*
    *** AN EMPTY LIST IS A CLAIM ***
    `isLoading` is false on a revisit, so checking only that would announce
    "no conversations" from a cached list that has not answered.
  */
  const settled = threads.isSuccess && !threads.isFetching
  const reallyEmpty = settled && list.length === 0

  const open = useMemo(
    () => list.find((t) => t.thread_id === openId) ?? null,
    [list, openId],
  )

  /*
    *** OPEN THE FIRST ONE, ON A WIDE SCREEN ONLY ***
    A two-column layout with an empty right half looks broken. On a phone the
    list IS the screen and auto-opening would hide it behind a conversation
    the person did not choose.
  */
  useEffect(() => {
    if (openId || list.length === 0) return
    if (window.matchMedia('(max-width: 52rem)').matches) return
    setOpenId(list[0]?.thread_id ?? null)
  }, [list, openId])

  /*
    Mark read when a thread with unread messages is opened. Keyed on the id
    and the count, so reopening an already-read thread sends nothing.
  */
  const markedRef = useRef<string | null>(null)
  useEffect(() => {
    if (!open || open.unread_count === 0) return
    if (markedRef.current === open.thread_id) return
    markedRef.current = open.thread_id
    markRead.mutate(open.thread_id)
  }, [open, markRead])

  /* A different thread may have unread messages later. */
  useEffect(() => {
    if (open && open.unread_count > 0) markedRef.current = null
  }, [open?.thread_id])

  /*
    *** OLDER PAGES LIVE HERE, NOT IN THE QUERY CACHE ***
    The query polls the newest 50 every ten seconds and REPLACES its array
    each time. Appending older pages to it would lose them on the next
    poll; merging them back would duplicate anything that arrived
    mid-scroll. So the poll owns the newest page and this owns the rest.
  */
  const [older, setOlder] = useState<ChatMessage[]>([])
  const [loadingOlder, setLoadingOlder] = useState(false)

  /* A new thread has none of the previous one's history. */
  useEffect(() => {
    setOlder([])
  }, [openId])

  /*
    *** NEWEST FIRST FROM THE SERVER, OLDEST FIRST ON SCREEN ***
    A conversation reads downward. Reversing here rather than asking the
    server keeps `next_cursor` meaningful — it is the id of the OLDEST row
    on the page.
  */
  const newest = messages.data?.messages ?? []
  const shown: ChatMessage[] = useMemo(
    () => [...older, ...newest].slice().reverse(),
    [older, newest],
  )

  /* The oldest id held, which is where the next page starts. */
  const oldestId = shown[0]?.message_id ?? null

  /*
    More to fetch when the SERVER said so on the newest page and nothing
    older has been loaded, or when the last older page said so.
  */
  const [hasOlder, setHasOlder] = useState(false)
  useEffect(() => {
    if (older.length === 0) setHasOlder(messages.data?.has_more ?? false)
  }, [messages.data?.has_more, older.length])

  const messagesRef = useRef<HTMLDivElement | null>(null)

  const loadOlder = async () => {
    if (!openId || !oldestId || loadingOlder) return
    setLoadingOlder(true)

    /*
      *** HOLD THE READER'S PLACE ***
      Prepending rows grows the scroll height, and the browser keeps
      `scrollTop` — so the view jumps upward by exactly the height added.
      Restoring the distance from the BOTTOM keeps the message they were
      reading under the cursor.
    */
    const box = messagesRef.current
    const before = box ? box.scrollHeight - box.scrollTop : 0

    try {
      const page = await fetchOlderMessages(openId, oldestId)
      setOlder((prev) => [...page.messages, ...prev])
      setHasOlder(page.has_more)

      requestAnimationFrame(() => {
        if (box) box.scrollTop = box.scrollHeight - before
      })
    } catch (caught) {
      toast.error(
        caught instanceof ApiError
          ? caught.message
          : 'Could not load older messages.',
      )
    } finally {
      setLoadingOlder(false)
    }
  }

  /*
    Scroll to the newest message when the thread changes, or when a new one
    arrives — but NOT when older pages are prepended, which `loadOlder`
    positions itself.
  */
  const endRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' })
  }, [openId, newest.length])

  const submit = async (body: string, attachmentId: string | null) => {
    if (!openId) return
    await send.mutateAsync({
      body: body.trim() || undefined,
      attachment_id: attachmentId ?? undefined,
    })
  }

  return (
    <PageStack>
      <PageHeader
        title="Messages"
        description={
          isMentor
            ? 'Conversations with your students.'
            : 'Conversations with your mentors.'
        }
      />

      {threads.isLoading && (
        <p className={styles.muted}>Loading your conversations…</p>
      )}

      {threads.error && (
        <p className={styles.error}>
          {threads.error instanceof ApiError
            ? threads.error.message
            : 'Could not load your conversations.'}
        </p>
      )}

      {reallyEmpty && (
        <Card>
          <p className={styles.muted}>
            {isMentor
              ? 'No conversations yet. A student starts one from their app or the portal.'
              : 'No conversations yet. Open one from a mentor after booking a session.'}
          </p>
        </Card>
      )}

      {list.length > 0 && (
        <div
          className={`${styles.layout} ${openId ? styles.layoutOpen : ''}`}
        >
          {/* ------------------------------------------------- the list */}
          <aside className={styles.threads} aria-label="Conversations">
            {list.map((t: ChatThread) => (
              <button
                key={t.thread_id}
                type="button"
                className={`${styles.thread} ${
                  t.thread_id === openId ? styles.threadOn : ''
                }`}
                onClick={() => setOpenId(t.thread_id)}
                aria-current={t.thread_id === openId}
              >
                <span className={styles.threadAvatar} aria-hidden="true">
                  {initials(t.other_party_name)}
                </span>
                <span className={styles.threadText}>
                  <span className={styles.threadTop}>
                    <strong className={styles.threadName}>
                      {t.other_party_name}
                    </strong>
                    <span className={styles.threadWhen}>
                      {threadTime(t.last_message_at)}
                    </span>
                  </span>
                  <span className={styles.threadSub}>
                    {t.is_locked ? 'Locked' : 'Tap to open'}
                  </span>
                </span>
                {/* Only when there is one. A grey 0 on every row is noise. */}
                {t.unread_count > 0 && (
                  <span className={styles.unread}>{t.unread_count}</span>
                )}
              </button>
            ))}
          </aside>

          {/* ----------------------------------------- the conversation */}
          <section className={styles.panel}>
            {!open && (
              <p className={styles.muted}>
                Choose a conversation to read it.
              </p>
            )}

            {open && (
              <>
                <header className={styles.panelHead}>
                  {/* Back to the list on a phone, where they are stacked. */}
                  <Button
                    variant="ghost"
                    className={styles.back}
                    onClick={() => setOpenId(null)}
                  >
                    ← All
                  </Button>
                  <span className={styles.panelName}>
                    {open.other_party_name}
                  </span>
                </header>

                <div className={styles.messages} ref={messagesRef}>
                  {messages.isLoading && (
                    <p className={styles.muted}>Loading messages…</p>
                  )}

                  {messages.data && shown.length === 0 && (
                    <p className={styles.muted}>
                      No messages yet. Say hello.
                    </p>
                  )}

                  {/*
                    *** ON DEMAND, NOT ON SCROLL ***
                    An infinite scroll that fires near the top would keep
                    firing while someone reads, pulling a long history in
                    whether or not they wanted it. A button loads one page
                    and says plainly that there is more.
                  */}
                  {hasOlder && (
                    <div className={styles.olderRow}>
                      <Button
                        variant="ghost"
                        onClick={loadOlder}
                        loading={loadingOlder}
                      >
                        Load older messages
                      </Button>
                    </div>
                  )}

                  {/* The beginning, once there is nothing left to fetch. */}
                  {!hasOlder && older.length > 0 && (
                    <p className={styles.olderNote}>
                      That is the start of this conversation.
                    </p>
                  )}

                  {shown.map((m) => (
                    <MessageBubble key={m.message_id} message={m} />
                  ))}
                  <div ref={endRef} />
                </div>

                <Composer
                  locked={open.is_locked}
                  isMentor={isMentor}
                  quota={quota.data ?? null}
                  threadId={open.thread_id}
                  sending={send.isPending}
                  onSend={submit}
                />
              </>
            )}
          </section>
        </div>
      )}
    </PageStack>
  )
}
